import { REBALANCE_PLANS_THEME } from '../common/riskProfileHeaderTheme'
import { useEffect } from 'react'
import BottomSheetContent from '../../../../../../../components/BottomSheetComponents/BottomSheetContent'
import { PrimaryButton } from '../../../../../../../components/execution/PrimaryButton'
import RenderHTML from '../../../../../../../components/ui-components/RenderHTML'
import { EventNames } from '../../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../../utils/appBridge'
import { formatPlanDateRange } from '../../../../../../../utils/utils'
import {
  PlansBottomSheet,
  PlansLumpsumListEventIds,
  PlansScreen
} from '../../../../analytics/plansEvents'
import { PlanUpdatedBottomSheetLines } from '../common/planUpdatedBottomSheetLines'

type RebalancePlanUpdatedBottomSheetProps = {
  planSnapshotDate: number
  planDurationInMonths?: number | null
  newRecommendationsCount?: number | null
  updatedRecommendationsCount?: number | null
  invalidatedRecommendationsCount?: number | null
  onDismiss: () => void
}

export function RebalancePlanUpdatedBottomSheet({
  planSnapshotDate,
  planDurationInMonths,
  newRecommendationsCount,
  updatedRecommendationsCount,
  invalidatedRecommendationsCount,
  onDismiss
}: RebalancePlanUpdatedBottomSheetProps) {
  const planMonthRangeLabel = formatPlanDateRange(
    planSnapshotDate,
    planDurationInMonths ?? 1
  ).replace(/ plan$/, '')

  useEffect(() => {
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        ScreenName: PlansScreen.LumpsumRebalance,
        eventId: PlansBottomSheet.PlanUpdated,
        bottomSheetName: PlansBottomSheet.PlanUpdated
      }
    })
  }, [])

  return (
    <BottomSheetContent
      onDismiss={onDismiss}
      bottomSheetHeadClassName="!pt-0"
      bottomSheetBodyClassName="!pt-6 !pb-4"
      bottomSheetCtas={
        <PrimaryButton
          label="View updated plan"
          onClick={() => {
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: PlansScreen.LumpsumRebalance,
                eventId: PlansLumpsumListEventIds.PlanUpdatedGotIt,
                buttonLabel: 'View updated plan'
              }
            })
            onDismiss()
          }}
          primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[12px]"
        />
      }
    >
      <div className="relative">
        <div className="pointer-events-none absolute -top-10 -right-5" aria-hidden>
          <PlanUpdatedBottomSheetLines />
        </div>
        <RenderHTML innerHTMLText={REBALANCE_PLAN_UPDATED_HEADER_ICON} className="mb-4 size-8" />
        <div>
          <div className="text-[16px] font-[400] leading-[130%] text-[#232323]">Plan updated for</div>
          <div className="text-[24px] font-[500] leading-[150%] text-[#232323]">
            {planMonthRangeLabel}
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-3">
          {newRecommendationsCount != null ? (
            <PlanUpdatedChangeRow
              iconHtml={PLAN_UPDATED_SPARKLE_ICON}
              highlight={`${newRecommendationsCount} new`}
              detail={newRecommendationsCount === 1 ? 'fund added' : 'funds added'}
            />
          ) : null}
          {updatedRecommendationsCount != null ? (
            <PlanUpdatedChangeRow
              iconHtml={PLAN_UPDATED_REFRESH_ICON}
              highlight={`${updatedRecommendationsCount} ${updatedRecommendationsCount === 1 ? 'fund' : 'funds'}`}
              detail="recommendations updated"
            />
          ) : null}
          {invalidatedRecommendationsCount != null ? (
            <PlanUpdatedChangeRow
              iconHtml={PLAN_UPDATED_CHECK_ICON}
              highlight={`${invalidatedRecommendationsCount} ${invalidatedRecommendationsCount === 1 ? 'fund' : 'funds'}`}
              detail={
                invalidatedRecommendationsCount === 1
                  ? 'no longer needs rebalancing'
                  : 'no longer need rebalancing'
              }
            />
          ) : null}
        </div>
      </div>
    </BottomSheetContent>
  )
}

type PlanUpdatedChangeRowProps = {
  iconHtml: string
  highlight: string
  detail: string
}

export function PlanUpdatedChangeRow({ iconHtml, highlight, detail }: PlanUpdatedChangeRowProps) {
  return (
    <div className="flex items-center gap-3">
      <RenderHTML innerHTMLText={iconHtml} className="size-4 shrink-0" />
      <p className="text-[16px] leading-[150%]">
        <span className="font-[500] text-[#232323]">{highlight}</span>{' '}
        <span className="font-[400] text-[#8E9393]">{detail}</span>
      </p>
    </div>
  )
}

const PLANS_INFO_ICON_STROKE = REBALANCE_PLANS_THEME.primary

const REBALANCE_PLAN_UPDATED_HEADER_ICON = `<svg width="32" height="32" viewBox="0 0 29.3386 29.3302" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5214 5.34453H3.27143C2.72204 5.34453 2.19518 5.56278 1.80671 5.95123C1.41824 6.33971 1.2 6.86658 1.2 7.41596V26.0588C1.2 26.6082 1.41824 27.1351 1.80671 27.5235C2.19518 27.9119 2.72204 28.1302 3.27143 28.1302H23.9857C25.1298 28.1302 26.0571 27.2029 26.0571 26.0588V15.7017" stroke="${PLANS_INFO_ICON_STROKE}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M1.2 11.5555H13.6286" stroke="${PLANS_INFO_ICON_STROKE}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M20.2512 4.0056L21.3079 1.43943C21.3675 1.29455 21.5088 1.2 21.6654 1.2C21.822 1.2 21.9633 1.29455 22.0229 1.43943L23.0796 4.0056C23.5003 5.02717 24.3114 5.83838 25.3331 6.25903L27.8992 7.31571C28.0442 7.37534 28.1386 7.51653 28.1386 7.67321C28.1386 7.8299 28.0442 7.97109 27.8992 8.03072L25.3331 9.0874C24.3114 9.50804 23.5003 10.3193 23.0796 11.3408L22.0229 13.907C21.9633 14.0519 21.822 14.1464 21.6654 14.1464C21.5088 14.1464 21.3675 14.0519 21.3079 13.907L20.2512 11.3408C19.8306 10.3193 19.0194 9.50804 17.9978 9.0874L15.4316 8.03072C15.2867 7.97109 15.1922 7.8299 15.1922 7.67321C15.1922 7.51653 15.2867 7.37534 15.4316 7.31571L17.9978 6.25903C19.0194 5.83838 19.8306 5.02717 20.2512 4.0056Z" stroke="${PLANS_INFO_ICON_STROKE}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`

const PLAN_UPDATED_SPARKLE_ICON = `<svg width="16" height="16" viewBox="0 0 14.3 14.3" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.79654 0.986708L5.75181 3.52387C5.33592 4.53389 4.53389 5.33592 3.52387 5.75181L0.986708 6.79654C0.843491 6.8555 0.75 6.99509 0.75 7.15C0.75 7.30491 0.843491 7.4445 0.986708 7.50346L3.52387 8.54819C4.53389 8.96408 5.33592 9.76611 5.75181 10.7761L6.79654 13.3133C6.8555 13.4566 6.99509 13.55 7.15 13.55C7.30491 13.55 7.4445 13.4566 7.50346 13.3133L8.54819 10.7761C8.96406 9.76611 9.76611 8.96408 10.7761 8.54819L13.3133 7.50346C13.4566 7.4445 13.55 7.30491 13.55 7.15C13.55 6.99509 13.4566 6.8555 13.3133 6.79654L10.7761 5.75181C9.76611 5.33592 8.96406 4.53389 8.54819 3.52387L7.50346 0.986708C7.4445 0.843491 7.30491 0.75 7.15 0.75C6.99509 0.75 6.8555 0.843491 6.79654 0.986708Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`

const PLAN_UPDATED_REFRESH_ICON = `<svg width="16" height="16" viewBox="0 0 13.4996 13.4969" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.8058 3.51898C10.7396 1.85375 8.87347 0.75 6.74946 0.75C3.43605 0.75 0.75 3.43605 0.75 6.74946" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M1.69377 9.97794C2.7599 11.6432 4.6261 12.7469 6.7501 12.7469C10.0636 12.7469 12.7496 10.0609 12.7496 6.74746" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.8261 1.21038V3.51786H9.51862" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M1.67232 12.286V9.97847H3.97981" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`

const PLAN_UPDATED_CHECK_ICON = `<svg width="16" height="16" viewBox="0 0 12.2633 11.0525" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.4133 0.850035L4.04112 10.2025L0.85002 6.38792" stroke="#232323" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>`

